<template>
  <div class="answerTextarea" :class="{'answerTextarea--lines':answer.display_options.textInputFormat===3, 'error':textLimitStyle, 'disabled-input': workStore.isWronglyAnswered && prevIsCorrectAnswer}" ref="rootRef">
    <template v-if="prevIsCorrectAnswer">
      <markdown-view :rawText="answerText(prevAnswerText)" :souruby="isSouruby"></markdown-view>
    </template>
    <template v-else>
      <virtual-keyboard v-if="isKeyboard" 
        :is-science="isScience" 
        :place-holder="answer.body.placeholder"
        :suffix="answer.body.suffix"
        :key="`key_${answer.id}`"
        :tabindex="tabIndex"
        :id="`answer-area-${answer.id}-${answer.sort_order}`"
        :prev-answer-text="prevAnswerText"
        :prev-is-correct-answer="prevIsCorrectAnswer"
        @press-enter="emit('press-enter')"
        @focus="prevIsCorrectAnswer ? null : emit('set-is-focus', answer.id)"
        @blur="emit('set-is-focus', null)"
        @set-latex="setLatex">
      </virtual-keyboard>
      <p v-else class="answerTextarea__input" :contenteditable="prevIsCorrectAnswer ? false : 'plaintext-only'" :placeholder="answer.body.placeholder"
         ref="textRef"
         :key="`keys_${answer.id}`"
         :tabindex="tabIndex"
         :id="`answer-area-${answer.id}-${answer.sort_order}`"
         :data-prev-is-correctAnswer="prevIsCorrectAnswer"
         v-text="restoreText"
         @drop.prevent
         @copy.prevent
         @paste.prevent
         @cut.prevent
         @click.right.prevent
         @keydown.enter.exact="isVertical || answer.display_options.textInputFormat!==3 ? $event.preventDefault() : true"
         @keyup.enter="enterKeyPress(answer)"
         @focus="prevIsCorrectAnswer ? null : emit('set-is-focus', answer.id)" 
         @blur="emit('set-is-focus', null)" 
         @input="inputText(answer)"
         @keydown="verticalArrowKeys($event)"
      >
      </p>
    </template>
  </div>
</template>


<script setup lang="ts">
  import {ref, onMounted, computed} from "vue"
  import {IQuiz} from "@/consts/interfaces"
  import {QUIZTYPE} from '@/consts/answer_column';
  import { useWorkStore } from '@/store/student/work_store';
  import {CHOICESLABEL} from "@/consts/choicesLabel";

  const textInput = ref("")
  const restoreText = ref(null)
  const textRef = ref(null)
  const rootRef = ref(null)
  const workStore = useWorkStore()

  const props = withDefaults(
    defineProps<{
      answer?: IQuiz;
      isPreSuffix?: boolean; //プレフィックス、サフィックスコンポーネントから来たかどうかフラグ
      isVertical?: boolean;
      isKeyboard?: boolean;
      isScience?: boolean;
      textLimit?: number;
      tabIndex?: number;
      prevAnswerText?: string;
      prevIsCorrectAnswer?: boolean;
      isSouruby?: boolean;
    }>(),
    {
      answer: null,
      isPreSuffix: false,
      isVertical: false,
      isKeyboard: false,
      isScience: false,
      textLimit: 500,
      tabIndex: 0,
      prevAnswerText: "",
      prevIsCorrectAnswer: false,
      isSouruby: false,
    }
  );
  
  const emit = defineEmits<{
    (e: 'is-error', id: boolean): void,
    (e: 'press-enter'): void,
    (e: 'input-pre-suffix-text', id: string): void,
    (e: 'input-text', id: string): void,
    (e: 'set-is-focus', id: any): void,
  }>()

  const textLimitStyle = computed(() => {
    if (props.answer.body.showCounter && props.answer.body.maxCharLimit){
      let result = textInput.value.length > props.answer.body.maxCharLimit
      emit('is-error', result)
      return result
    }
  })

  onMounted(() => {
    restoreText.value = props.prevAnswerText
  })

  // NFR-5442 正答済み表示する時フォーマット修正を行う。
  function answerText(text:string) {
    if(props.isScience && props.prevIsCorrectAnswer){
      // 正答済みで理科の場合、上付き・下付き文字を正しく表示する
      let isSubSup =(/\_|\^/g).test(text)
      if(isSubSup){
        text = text.replace(/(\$)(.*?)(\$)/g, '$\\ce{$2}$')
      }
      if(props.answer.answer_format == QUIZTYPE.DESCRIPTION || props.answer.answer_format == QUIZTYPE.WRITTEN_RANDOM_ORDER){
        // 物理キーボードで化学式を直接に入力する時プラス記号の前後にスペースを入れる。
        text = text.replace(/([0-9A-Za-z\+\-\}])(\+)/g, '$1 $2 ')
        // 理科の場合、アルファベットは正体にする必要があるので別にフォーマットを変更する
        text = text.replace(/[\$](\\|[0-9]*)([A-Za-z])/g, "$\\rm $1$2")
      }
    }
    // 並び替えでchoicesLabelが原文のままではない場合、設定したラベルを表示する。
    if(props.answer.answer_format == QUIZTYPE.SORTING && props.answer.display_options.choicesLabel !== 1){
      let new_answer = text.split(props.isVertical?'⇓':'⇒')
      text = new_answer.map(item => CHOICESLABEL[props.answer.display_options.choicesLabel][props.answer.body.answers.map(i => i.answerText).indexOf(item)]).join(props.isVertical?'⇓':'⇒')
    }
    return text
  }

  function verticalArrowKeys(e) {
    let charCode = e.which; // 押下したボタン
    let sel = window.getSelection() // ユーザーが選択した文字列の範囲を取得
    let textNode: any = sel.focusNode // selection の終点のある node
    let childNodes = e.target.childNodes // 選択したnodeを含め全nodeを取得（複数行対応のため必要）
    let currentNodeIndex = Array.from(childNodes).findIndex(node => node == textNode) // childNodesから現在nodeのindexを取得
    let newOffset: number = 0

    if (props.isVertical) { // 縦書きの場合のみ矢印キー動きを制御
      if (charCode == 37) { // 左キー
        e.preventDefault()
        if(textNode.data){
          leftRightArrowKeysControl(charCode, childNodes, currentNodeIndex, sel)
        }
      } else if (charCode == 38) { // 上キー
        e.preventDefault()
        newOffset = sel.focusOffset - 1
        if (newOffset >= 0) { // 一文字ずつキャレットを上に移動
          sel.collapse(textNode, newOffset)
        }else{ // 先頭に着くと複数行の場合は次のノードの一番下に移動
          let nextNodeIndex = currentNodeIndex - 1
          if (nextNodeIndex >= 0){
            sel.collapse(childNodes[nextNodeIndex], childNodes[nextNodeIndex].length - 1)
          }
        }
      } else if (charCode == 39) { // 右キー
        e.preventDefault()
        if(textNode.data){
          leftRightArrowKeysControl(charCode, childNodes, currentNodeIndex, sel)
        }
      } else if (charCode == 40) { // 下キー
        e.preventDefault()
        newOffset = sel.focusOffset + 1
        if (newOffset <= textNode.length) { // 一文字ずつキャレットを下に移動
          sel.collapse(textNode, newOffset)
        }else{ // 一番下に着くと複数行の場合は次のノードの先頭に移動
          let nextNodeIndex = currentNodeIndex + 1
          if(childNodes[nextNodeIndex] && nextNodeIndex <= childNodes.length){
            // NFR-4667, iPad store `<br>` instead of `\n` if press enter
            if(childNodes[nextNodeIndex].nodeName == 'BR'){
              sel.collapse(childNodes[nextNodeIndex+1], 0)
            } else {
              sel.collapse(childNodes[nextNodeIndex], 1)
            }
          }
        }
      }
    }
  }

  function leftRightArrowKeysControl(charCode, childNodes, currentNodeIndex, sel){
    let childArray = [] // 改行ノードがある前のノードをグループとした配列,要素1個が1行(ex:[node,node,改行,node,改行]→[[node,node], [node]])
    let tempArray = [] // 改行ノードが来る前までのノードを一時的に保存
    let nodeTotalLength = 0 // 改行ノードが来る前までのノード全部の長さ
    let nextNode = 0

    // 改行ノードを除いたノードを配列に入れる。（1行ずつ）
    // backspaceを使うとnodeListで改行が無くなって「node,改行,node,改行,node」の形ではなく「node,node,改行,node」になって1行で複数のノードが含まれる
    for (let i = 0; i < childNodes.length; i++) {
      // NFR-4667, iPad store `<br>` instead of `\n` if press enter
      if (childNodes[i].nodeName == 'BR' || !childNodes[i].data.trim()) {
        if (!childNodes[i].nextSibling){
          tempArray.push(childNodes[i])
        }
        childArray.push({nodes: tempArray, totalLength: nodeTotalLength})
        tempArray = []
        nodeTotalLength = 0
      } else {
        nodeTotalLength += childNodes[i].length
        tempArray.push(childNodes[i])
        if (!childNodes[i].nextSibling) {
          childArray.push({nodes: tempArray, totalLength: nodeTotalLength})
        }
      }
    }

    for (let i = 0; i < childArray.length; i++) {
      // 改行ノードを除いたノード配列の中で現在のノードが入ってるindexを取得
      let index = childArray[i]["nodes"].findIndex(node => node == childNodes[currentNodeIndex])
      if (index != -1) {
        let currentOffSet = 0
        let tempLength = 0
        for (let j = 0; j < childArray[i]["nodes"].length; j++) {
          if (j < index) { // 現在ノード前にノードがある場合、前のノードの長さを足す
            currentOffSet += childArray[i]["nodes"][j].length
          }
        }
        // 現在カーソルがある位置を取得（1行の中に複数のノードが存在する場合あるため現在のoffsetを計算して取得する必要がある、）
        currentOffSet += sel.anchorOffset

        if(charCode == 37){ // 左キーが押された場合
          nextNode = i + 1
        }else if(charCode == 39){ // 右キーが押された場合
          nextNode = i - 1
        }
        if (childArray[nextNode]) { // 次のノードが存在する場合
          if (currentOffSet >= childArray[nextNode]["totalLength"]) { // 現在のoffsetが次のノードのトータル長さより大きい場合、次のノードの一番下にカーソル移動
            sel.collapse(childArray[nextNode]["nodes"][childArray[nextNode]["nodes"].length - 1], childArray[nextNode]["nodes"][childArray[nextNode]["nodes"].length - 1].length)
          } else { // 次のノードのトータル長さ大きい場合
            for (let k = 0; k < childArray[nextNode]["nodes"].length; k++) {
              tempLength += childArray[nextNode]["nodes"][k].length
              if (currentOffSet <= tempLength) {
                sel.collapse(childArray[nextNode]["nodes"][k], childArray[nextNode]["nodes"][k].length - (tempLength - currentOffSet))
                return
              }
            }
          }
        } else { // 次のノードがない場合
          if(charCode == 37){
            sel.collapse(childArray[i]["nodes"][childArray[i]["nodes"].length - 1], childArray[i]["nodes"][childArray[i]["nodes"].length - 1].length)
          }else if(charCode == 39){
            sel.collapse(childArray[i]["nodes"][0], 0)
          }
        }
      }
    }
  }

  function enterKeyPress(answer){
    // for single line enter key press
    if(answer.display_options.textInputFormat !==3){
      emit('press-enter')
    }
  }

  function inputText(answer) {
    let countTextArea: any = textRef.value
    // NFR-2859 countTextArea.textContent not found from safari browser
    if(!countTextArea) {
      return
    }
    textInput.value = countTextArea.textContent
    // check if input field have line break or enter key press
    let match = /\r|\n/.exec(countTextArea.innerHTML);
    // single line input enter press delete line break from input
    if(match && answer.display_options.textInputFormat !==3){
      countTextArea.innerHTML = countTextArea.innerHTML.replace(/\r|\n/g,'')
      return
    }
    if(countTextArea.innerHTML == "<br>"){
      countTextArea.innerHTML = ""
    }
    if(countTextArea.textContent.length > props.textLimit){
      countTextArea.textContent = countTextArea.textContent.substr(0, props.textLimit)
    }
    if (props.isPreSuffix) { // プレフィックス、サフィックスがあるテキスト
      if (answer.body.showCounter){
        let countText: any = countTextArea.parentElement.parentElement.previousElementSibling.firstElementChild
        countText.textContent = countTextArea.textContent.length.toString()
        if(props.answer.body.maxCharLimit && countTextArea.textContent.length>props.answer.body.maxCharLimit){
          countText.setAttribute("style","color: #e75821");
        }else{
          countText.setAttribute("style","");
        }
      }
      emit('input-pre-suffix-text', countTextArea.textContent)
    } else {
      if (answer.body.showCounter) { // 文字制限テキスト
        let countText: any = countTextArea.parentElement.previousElementSibling.firstElementChild
        countText.textContent = countTextArea.textContent.length.toString()
        if(props.answer.body.maxCharLimit && countTextArea.textContent.length>props.answer.body.maxCharLimit){
          countText.style = {color: '#e75821'}
          countText.setAttribute("style","color: #e75821");
        }else{
          countText.setAttribute("style","");
        }
      }
      emit('input-text', countTextArea.textContent)
    }
  }
  function isJapanese(ch) {
    return (
      (ch >= "\u4e00" && ch <= "\u9faf") ||
      (ch >= "\u3400" && ch <= "\u4dbf") ||
      (ch >= "\u3040" && ch <= "\u309f") ||
      (ch >= "\u30a0" && ch <= "\u30ff") ||
      (ch == "\u223d") || // NFR-3011 「∽」記号を日本語扱いにして$サインに囲まれないようにする
      (ch == "\u3001" || ch == "\u3002") // 理科で正体処理する時「 、。」これが「＄」中で入るので「 、。」これも日本語扱いにする。
    );
  }
  function setLatex(value){
    value = value.replace(/({\\text{\\textsf{\\backsim}}})|({\\textsf{\\backsim}})/g, "∽") // NFR-3011 sans-serifフォントに変更した記号を「∽」記号に変換する。
    let new_value:string = "";
    let len:number = value.length;
    let p:boolean = false;
    for (let i = 0; i < len; i++) {
      let isJp = isJapanese(value[i]);
      if (i === 0) {
        if (isJp) {
          new_value += value[i];
          p = true;
        } else {
          new_value += "$" + value[i];
        }
      } else {
        if (p === true && isJp === true) {
          new_value += value[i];
        } else if (p === true && isJp === false) {
          p = false;
          new_value += "$" + value[i];
        } else if (p === false && isJp === true) {
          p = true;
          new_value += "$" + value[i];
        } else {
          new_value += value[i];
        }
      }
    }
    if (len && !isJapanese(value[len - 1])) {
      new_value += "$";
    }
    // replace physical keyboard frac to dfrac
    new_value = new_value.replace(/(\\frac)/g, "\\dfrac")
    if (new_value.length > props.textLimit) {
      new_value = new_value.substr(0, props.textLimit-1) + "$";
    }
    if (props.isPreSuffix) {
      emit('input-pre-suffix-text', new_value)
    }else{
      emit('input-text', new_value)
    }
    // set char maxLimit counter
    if(props.answer.body.showCounter){
      textInput.value = value  // for show warning red border
      let countText: any = null
      if(props.isPreSuffix){
        countText = rootRef.value.parentElement.parentElement.getElementsByClassName('molecule').item(0)
      } else {
        countText = rootRef.value.parentElement.getElementsByClassName('molecule').item(0)
      }
      if(countText){
        countText.textContent = len.toString()
        if(props.answer.body.maxCharLimit && len>props.answer.body.maxCharLimit){
          countText.style = {color: '#e75821'}
          countText.setAttribute("style","color: #e75821");
        }else{
          countText.setAttribute("style","");
        }
      }
    }
  }
</script>
